// ABOUTME: Investigations list page showing all user investigations.
// ABOUTME: Clean, minimal design with proper delete confirmation.

import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, MessageSquare, Search, Trash2, RefreshCw, Pin, Database } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { useTour } from '@/context/TourContext';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { UpgradeBanner } from '@/components/billing/UpgradeBanner';
import { DatasourcePickerModal } from '@/components/investigation/DatasourcePickerModal';
import { billingApi } from '@/services/api/billingClient';
import { apiFetch } from '@/services/api/client';
import { useState, useEffect, useCallback } from 'react';
import type { DataSource } from '@/types/api';

export function InvestigationsPage() {
  const navigate = useNavigate();
  const { sessions, startNewSession, deleteSession, refreshSessions, isLoadingSessions, pinnedItems, isFreeTier, currentWorkspaceId } = useApp();
  const { isRunning: isTourRunning, currentAction, onInvestigationCreated } = useTour();
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Proactive gating state for freemium users
  const [showCapPrompt, setShowCapPrompt] = useState(false);
  const [capInfo, setCapInfo] = useState<{ used: number; limit: number } | null>(null);

  // Refresh sessions every time the page is visited
  useEffect(() => {
    refreshSessions();
  }, [refreshSessions]);

  // Build a map of pin counts per investigation
  const pinCountByInvestigation = pinnedItems.reduce((acc, pin) => {
    const invId = pin.sourceInvestigationId;
    acc[invId] = (acc[invId] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Map sessions to investigation list format - use raw string to avoid timezone issues
  const allInvestigations = sessions.map(s => ({
    id: s.id,
    title: s.title,
    updated_at: s.lastActiveRaw, // Use raw string from API
    message_count: s.messageCount ?? s.messages.length,
    pin_count: pinCountByInvestigation[s.id] || 0,
    dataSourceIds: s.dataSourceIds || [],
  }));

  // Filter and sort by most recently active
  const filteredInvestigations = allInvestigations
    .filter((inv) => inv.title.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => new Date(b.updated_at + 'Z').getTime() - new Date(a.updated_at + 'Z').getTime());

  // Datasource picker state
  const [showPicker, setShowPicker] = useState(false);
  const [availableDataSources, setAvailableDataSources] = useState<DataSource[]>([]);
  // Map of data source ID -> name for displaying on investigation cards
  const [dataSourceMap, setDataSourceMap] = useState<Record<string, string>>({});
  // Track when data sources were last fetched to avoid redundant requests
  const [dsLastFetchedFor, setDsLastFetchedFor] = useState<string | null>(null);

  const fetchDataSources = useCallback(async () => {
    try {
      // Fetch workspace data sources via user profile endpoint
      const profile = await apiFetch<{
        primary_workspace_id: string;
        workspaces: Array<{ workspace_id: string; data_sources?: DataSource[] }>;
      }>('/users/me');
      const ws = profile.workspaces?.find(
        (w) => w.workspace_id === currentWorkspaceId
      ) ?? profile.workspaces?.find(
        (w) => w.workspace_id === profile.primary_workspace_id
      ) ?? profile.workspaces?.[0];
      const allSources: DataSource[] = ws?.data_sources ?? [];

      // Build ID -> name map from all sources (including demo, for display)
      const dsMap: Record<string, string> = {};
      for (const ds of allSources) {
        dsMap[ds.id] = ds.name;
      }
      setDataSourceMap(dsMap);

      // Only show connected sources
      const connectedSources = allSources.filter((ds) => ds.status === 'connected');
      // Demo visibility: hide demo when real sources exist
      const realSources = connectedSources.filter((ds) => !ds.is_demo);
      const filtered = realSources.length > 0
        ? connectedSources.filter((ds) => !ds.is_demo)
        : connectedSources;
      setAvailableDataSources(filtered);
      setDsLastFetchedFor(currentWorkspaceId);
    } catch {
      // Reset both on failure so stale data doesn't persist
      setAvailableDataSources([]);
      setDataSourceMap({});
    }
  }, [currentWorkspaceId]);

  // Fetch data sources on mount for name resolution, but skip if already fetched for this workspace
  useEffect(() => {
    if (dsLastFetchedFor !== currentWorkspaceId) {
      fetchDataSources();
    }
  }, [fetchDataSources, dsLastFetchedFor, currentWorkspaceId]);

  const handleNewInvestigation = async () => {
    // Proactive gating: check usage before creating for freemium users
    if (isFreeTier) {
      try {
        const usage = await billingApi.getUsage();
        const invUsage = usage.usage.active_investigations;
        if (invUsage.used >= invUsage.limit) {
          setCapInfo({ used: invUsage.used, limit: invUsage.limit });
          setShowCapPrompt(true);
          return;
        }
      } catch (err) {
        // If usage check fails, allow creation to proceed
        console.error('[InvestigationsPage] Failed to check usage:', err);
      }
    }

    // During tour, skip the picker and create directly
    if (isTourRunning) {
      await createInvestigation();
      return;
    }

    // Show datasource picker before creating
    await fetchDataSources();
    setShowPicker(true);
  };

  const createInvestigation = async (dataSourceIds?: string[]) => {
    setShowPicker(false);
    setIsCreating(true);
    try {
      const newId = await startNewSession(isTourRunning, dataSourceIds);
      // Notify tour if it's waiting for this action
      if (isTourRunning && currentAction === 'click-new-investigation') {
        onInvestigationCreated(newId);
      }
      navigate(`/investigations/${newId}`);
    } catch (err) {
      console.error('Failed to create investigation:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, investigation: { id: string; title: string }) => {
    e.stopPropagation();
    setDeleteTarget(investigation);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);
    try {
      await deleteSession(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err) {
      console.error('Failed to delete investigation:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Format relative time - dateString is UTC without Z suffix from API
  const formatRelativeTime = (dateString: string) => {
    // Append Z if no timezone specified to parse as UTC
    const normalized = dateString.endsWith('Z') || dateString.includes('+')
      ? dateString
      : dateString + 'Z';
    const date = new Date(normalized);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="h-full overflow-y-auto">
      {/* Datasource Picker Modal */}
      <DatasourcePickerModal
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onConfirm={createInvestigation}
        dataSources={availableDataSources}
      />

      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="heading-page">
              Investigations
            </h1>
            <p className="text-sm text-surface-400 mt-1">
              {allInvestigations.length} investigation{allInvestigations.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={refreshSessions}
              disabled={isLoadingSessions}
            >
              <RefreshCw className={`w-4 h-4 ${isLoadingSessions ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              onClick={handleNewInvestigation}
              leftIcon={<Plus className="w-4 h-4" />}
              disabled={isCreating}
              data-tour="new-investigation"
            >
              {isCreating ? 'Creating...' : 'New'}
            </Button>
          </div>
        </div>

        <UpgradeBanner pageKey="investigations" message="Unlock up to 20 active investigations with Pro" />

        {/* Investigation Cap Prompt — shown when freemium user is at active investigation limit */}
        {showCapPrompt && capInfo && (
          <div
            data-testid="investigation-cap-prompt"
            className="mb-6 p-4 card border border-primary-100 bg-primary-50 rounded-sm"
          >
            <h3 className="text-sm font-medium text-surface-800 mb-1">
              Investigation limit reached
            </h3>
            <p className="text-sm text-surface-600 mb-3">
              Your Free plan includes {capInfo.limit} active investigations. Upgrade to Pro for 20.
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => navigate('/upgrade')}
              >
                Upgrade
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCapPrompt(false)}
              >
                Dismiss
              </Button>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <Input
            placeholder="Search investigations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftIcon={<Search className="w-4 h-4" />}
          />
        </div>

        {/* Loading State */}
        {isLoadingSessions && allInvestigations.length === 0 && (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-6 h-6 text-primary-500 animate-spin" />
          </div>
        )}

        {/* Investigations List */}
        {!isLoadingSessions && (
          <div className="space-y-2">
            {filteredInvestigations.length > 0 ? (
              filteredInvestigations.map((investigation, index) => (
                <motion.div
                  key={investigation.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => navigate(`/investigations/${investigation.id}`)}
                  className="group flex items-center gap-4 px-4 py-3.5 card-outlined hover:bg-surface-50 cursor-pointer transition-all duration-200"
                >
                  <div className="w-9 h-9 rounded-sm bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-4 h-4 text-primary-600" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-surface-800 truncate">
                      {investigation.title}
                    </h3>
                    {investigation.dataSourceIds.length > 0 && Object.keys(dataSourceMap).length > 0 && (
                      <div className="flex items-center gap-1.5 mt-1">
                        {investigation.dataSourceIds.map((dsId) => (
                          <span
                            key={dsId}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs text-surface-500 bg-surface-100 rounded"
                          >
                            <Database className="w-3 h-3" />
                            {dataSourceMap[dsId] || 'Unknown source'}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-surface-400">
                        {investigation.message_count || 0} messages
                      </span>
                      <span className="text-surface-300">·</span>
                      <span className="text-xs text-surface-400">
                        {formatRelativeTime(investigation.updated_at)}
                      </span>
                      {investigation.pin_count > 0 && (
                        <>
                          <span className="text-surface-300">·</span>
                          <span className="text-xs text-surface-400 flex items-center gap-1">
                            <Pin className="w-3 h-3" />
                            {investigation.pin_count}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={(e) => handleDeleteClick(e, investigation)}
                    className="p-2 rounded-sm opacity-0 group-hover:opacity-100 hover:bg-error-light transition-all"
                    aria-label="Delete investigation"
                  >
                    <Trash2 className="w-4 h-4 text-surface-400 hover:text-error transition-colors" />
                  </button>
                </motion.div>
              ))
            ) : allInvestigations.length === 0 ? (
              <div className="empty-state">
                <div className="w-14 h-14 rounded-sm bg-primary-50 flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-7 h-7 text-primary-500" />
                </div>
                <h3 className="empty-state-title">
                  No investigations yet
                </h3>
                <p className="empty-state-description">
                  Start asking questions about your data to create your first investigation.
                </p>
                <Button onClick={handleNewInvestigation} size="sm">
                  Start Investigating
                </Button>
              </div>
            ) : (
              <div className="text-center py-12">
                <Search className="w-6 h-6 text-surface-400 mx-auto mb-3" />
                <p className="text-sm text-surface-400">
                  No investigations match "{searchQuery}"
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Investigation"
        message={`Are you sure you want to delete "${deleteTarget?.title}"? This will also delete all associated pins. This action cannot be undone.`}
        confirmLabel="Delete"
        isLoading={isDeleting}
      />
    </div>
  );
}
